// openEHR LANG model — BMM package org.openehr.lang.bmm.core.feature
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { BMM_CLASS, BMM_CONTAINER_TYPE, BMM_INDEXED_CONTAINER_TYPE, BMM_STATUS_TYPE, BMM_TYPE, BMM_UNITARY_TYPE } from "../../_shared.ts";
import { BMM_PROCEDURE_TYPE } from "../../_shared.ts";
import { BMM_FORMAL_ELEMENT } from "../core.ts";
import type { BMM_STATEMENT_BLOCK } from "../statement.ts";
import type { BMM_LITERAL_VALUE } from "./literal_value.ts";
import * as openehr_base from "../../../base/mod.ts";

/**
 * A module-scoped formal element.
 */
export abstract class BMM_FEATURE extends BMM_FORMAL_ELEMENT {
  /**
   * Internal storage for is_synthesised_generic
   * @protected
   */
  protected _is_synthesised_generic?: openehr_base.Boolean;

  /**
   * True if this feature was synthesised due to generic substitution in an inherited type, or further constraining of a formal generic parameter.
   */
  get is_synthesised_generic(): boolean | undefined {
    return this._is_synthesised_generic?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_synthesised_generic.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_synthesised_generic(): openehr_base.Boolean | undefined {
    return this._is_synthesised_generic;
  }

  /**
   * Sets is_synthesised_generic from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_synthesised_generic(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_synthesised_generic = undefined;
    } else if (typeof val === "boolean") {
      this._is_synthesised_generic = openehr_base.Boolean.from(val);
    } else {
      this._is_synthesised_generic = val;
    }
  }

  /**
   * Extensions to feature-level meta-types.
   */
  feature_extensions?: undefined;
  /**
   * Group containing this feature.
   */
  group?: BMM_FEATURE_GROUP;
  /**
   * Model element within which an element is declared.
   */
  override scope?: BMM_CLASS = undefined;
}

/**
 * Meta-type representing instantiable features, i.e. features that are created as value objects.
 */
export abstract class BMM_INSTANTIABLE_FEATURE extends BMM_FEATURE {
}

/**
 * Meta-type for static (i.e. read-only) properties.
 */
export abstract class BMM_STATIC extends BMM_INSTANTIABLE_FEATURE {
}

/**
 * An immutable, static value-returning element scoped to a class. The \`_value_\` is the result of the evaluation of the \`_generator_\`, which may be as simple as a literal value, or may be any expression, including a function call.
 */
export class BMM_CONSTANT extends BMM_STATIC {
  /**
   * Literal value of the constant.
   */
  generator?: BMM_LITERAL_VALUE<any>;
}

/**
 * Meta-type of a writable property definition within a class definition of an object model. The \`_is_composition_\` attribute indicates whether the property has sub-part or an association semantics with respect to the owning class.
 */
export abstract class BMM_PROPERTY extends BMM_INSTANTIABLE_FEATURE {
  /**
   * Internal storage for is_im_runtime
   * @protected
   */
  protected _is_im_runtime?: openehr_base.Boolean;

  /**
   * True if this property is marked with info model \`_im_runtime_\` property.
   */
  get is_im_runtime(): boolean | undefined {
    return this._is_im_runtime?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_im_runtime.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_im_runtime(): openehr_base.Boolean | undefined {
    return this._is_im_runtime;
  }

  /**
   * Sets is_im_runtime from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_im_runtime(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_im_runtime = undefined;
    } else if (typeof val === "boolean") {
      this._is_im_runtime = openehr_base.Boolean.from(val);
    } else {
      this._is_im_runtime = val;
    }
  }

  /**
   * Internal storage for is_im_infrastructure
   * @protected
   */
  protected _is_im_infrastructure?: openehr_base.Boolean;

  /**
   * True if this property was marked with info model \`_im_infrastructure_\` flag.
   */
  get is_im_infrastructure(): boolean | undefined {
    return this._is_im_infrastructure?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_im_infrastructure.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_im_infrastructure(): openehr_base.Boolean | undefined {
    return this._is_im_infrastructure;
  }

  /**
   * Sets is_im_infrastructure from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_im_infrastructure(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_im_infrastructure = undefined;
    } else if (typeof val === "boolean") {
      this._is_im_infrastructure = openehr_base.Boolean.from(val);
    } else {
      this._is_im_infrastructure = val;
    }
  }

  /**
   * Internal storage for is_composition
   * @protected
   */
  protected _is_composition?: openehr_base.Boolean;

  /**
   * True if this property instance is a compositional sub-part of the owning class instance. Equivalent to 'composition' in UML associations (but missing from UML properties without associations) and also 'cascade-delete' semantics in ER schemas.
   */
  get is_composition(): boolean | undefined {
    return this._is_composition?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_composition.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_composition(): openehr_base.Boolean | undefined {
    return this._is_composition;
  }

  /**
   * Sets is_composition from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_composition(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_composition = undefined;
    } else if (typeof val === "boolean") {
      this._is_composition = openehr_base.Boolean.from(val);
    } else {
      this._is_composition = val;
    }
  }

  /**
   * Interval form of \`0..1\`, \`1..1\` etc, derived from \`_is_nullable_\`.
   * @returns Result value
   */
  existence(): openehr_base.Multiplicity_interval {
    // Create interval based on is_nullable flag
    // If nullable: 0..1, else 1..1
    const lower = this.is_nullable ? 0 : 1;
    const upper = 1;
    const interval = new openehr_base.Multiplicity_interval();
    interval.lower = openehr_base.Integer.from(lower);
    interval.upper = openehr_base.Integer.from(upper);
    interval.lower_included = true;
    interval.upper_included = true;
    interval.lower_unbounded = false;
    interval.upper_unbounded = false;
    return interval;
  }

  /**
   * Name of this property to display in UI.
   * @returns Result value
   */
  display_name(): openehr_base.String {
    // Format: name: TypeName
    const propName = this.name || "unnamed";
    const typeName = this.type?.type_name
      ? this.type.type_name()?.value || "Any"
      : "Any";
    return openehr_base.String.from(`${propName}: ${typeName}`);
  }
}

/**
 * Meta-type of for properties of linear container type, such as List<T> etc.
 */
export class BMM_CONTAINER_PROPERTY extends BMM_PROPERTY {
  /**
   * Cardinality of this container.
   */
  cardinality?: openehr_base.Multiplicity_interval;
  /**
   * Declared or inferred static type of the entity.
   */
  override type?: BMM_CONTAINER_TYPE = undefined;
  /**
   * Name of this property in form \`name: ContainerTypeName<>\`.
   * @returns Result value
   */
  display_name(): openehr_base.String {
    // Format: name: ContainerTypeName<ElementType>
    const propName = this.name || "unnamed";
    const containerTypeName = this.type?.type_name
      ? this.type.type_name()?.value || "Container"
      : "Container";
    return openehr_base.String.from(`${propName}: ${containerTypeName}`);
  }
}

/**
 * A feature defining a routine, scoped to a class.
 */
export abstract class BMM_ROUTINE extends BMM_FEATURE {
  /**
   * Formal parameters of the routine.
   */
  parameters?: BMM_PARAMETER[];
  /**
   * Boolean conditions that must evaluate to True for the routine to execute correctly, May be used to generate exceptions if included in run-time build.
   *
   * A False pre-condition implies an error in the passed parameters.
   */
  pre_conditions?: undefined;
  /**
   * Boolean conditions that will evaluate to True if the routine executed correctly, May be used to generate exceptions if included in run-time build.
   *
   * A False post-condition implies an error (i.e. bug) in routine code.
   */
  post_conditions?: undefined;
  /**
   * Body of a routine, i.e. executable program.
   */
  definition?: BMM_ROUTINE_DEFINITION;
  /**
   * Return number of arguments of this routine.
   * @returns Result value
   */
  arity(): openehr_base.Integer {
    // Return the count of parameters
    const paramCount = this.parameters?.length || 0;
    return openehr_base.Integer.from(paramCount);
  }
}

/**
 * A formal element with signature of the form: \`name ({arg:TArg}*):TResult\`. A function is a computed (rather than data) element, generally assumed to be non-state-changing.
 */
export class BMM_FUNCTION extends BMM_ROUTINE {
  /**
   * Optional details enabling a function to be represented as an operator in a syntactic representation.
   */
  operator_definition?: BMM_OPERATOR;
  /**
   * Automatically created Result variable, usable in body and post-condition.
   */
  result?: BMM_RESULT;
}

/**
 * A routine-scoped formal element.
 */
export abstract class BMM_VARIABLE extends BMM_FORMAL_ELEMENT {
  /**
   * Routine within which variable is defined.
   */
  override scope?: BMM_ROUTINE = undefined;
}

/**
 * Meta-type for writable variables, including the special variable \`Result\`.
 */
export abstract class BMM_WRITABLE_VARIABLE extends BMM_VARIABLE {
}

/**
 * A routine local variable (writable).
 */
export class BMM_LOCAL extends BMM_WRITABLE_VARIABLE {
}

/**
 * Definition of a symbolic operator associated with a function.
 */
export class BMM_OPERATOR {
  /**
   * Position of operator in syntactic representation.
   */
  position?: BMM_OPERATOR_POSITION;
  /**
   * Set of \`String\` symbols that may be used to represent this operator in a textual representation of a BMM model.
   */
  symbols?: undefined;
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Formal name of the operator, e.g. 'minus' etc.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }
}

/**
 * Meta-type for writable variables, including routine parameters and the special variable \`Self\`.
 */
export abstract class BMM_READONLY_VARIABLE extends BMM_VARIABLE {
}

/**
 * A routine parameter variable (read-only).
 */
export class BMM_PARAMETER extends BMM_READONLY_VARIABLE {
  /**
   * Optional read/write direction of the parameter. If none-supplied, the parameter is treated as \`in\`, i.e. readable.
   */
  direction?: BMM_PARAMETER_DIRECTION;
}

/**
 * A formal element with signature of the form: \`name ({arg:TArg}*):TStatus\`, where \`TStatus\` is the built-in type \`BMM_STATUS_TYPE\`.. A procedure is a computed (rather than data) element, generally assumed to be state-changing, and is usually called in the form \`name ({arg:TArg}*)\`.
 */
export class BMM_PROCEDURE extends BMM_ROUTINE {
  /**
   * Declared or inferred static type of the entity.
   */
  override type?: BMM_STATUS_TYPE = undefined;
  /**
   * Formal signature of this element, in the form:
   *
   * \`name [arg1_name: T_arg1, ...][:T_value]\`
   *
   * Specific implementations in descendants.
   * @returns Result value
   */
  signature(): BMM_PROCEDURE_TYPE {
    // Create and return a BMM_PROCEDURE_TYPE representing this procedure's signature
    const sig = new BMM_PROCEDURE_TYPE();
    // Set argument types from parameters
    if (this.parameters && this.parameters.length > 0) {
      sig.argument_types = this.parameters.map((p) => p.type).filter(
        Boolean,
      ) as BMM_TYPE[];
    }
    return sig;
  }
}

/**
 * Meta-type of for properties of unitary type.
 */
export class BMM_UNITARY_PROPERTY extends BMM_PROPERTY {
  /**
   * Declared or inferred static type of the entity.
   */
  override type?: BMM_UNITARY_TYPE = undefined;
}

/**
 * Meta-type of for properties of linear container type, such as \`Hash<Index_type, T>\` etc.
 */
export class BMM_INDEXED_CONTAINER_PROPERTY extends BMM_CONTAINER_PROPERTY {
  /**
   * Declared or inferred static type of the entity.
   */
  override type?: BMM_INDEXED_CONTAINER_TYPE = undefined;
  /**
   * Name of this property in form \`name: ContainerTypeName<IndexTypeName, ...>\`.
   * @returns Result value
   */
  display_name(): openehr_base.String {
    // Format: name: ContainerTypeName<IndexType, ElementType>
    const propName = this.name || "unnamed";
    const containerTypeName = this.type?.type_name
      ? this.type.type_name()?.value || "IndexedContainer"
      : "IndexedContainer";
    return openehr_base.String.from(`${propName}: ${containerTypeName}`);
  }
}

/**
 * Automatically declared variable representing result of a Function call (writable).
 */
export class BMM_RESULT extends BMM_WRITABLE_VARIABLE {
  /**
   * Name of this model element.
   */
  override get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  override get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  override set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }
}

/**
 * Abstract parent of feature extensions.
 */
export abstract class BMM_FEATURE_EXTENSION {
}

/**
 * Abstract ancestor of routine body meta-types.
 */
export abstract class BMM_ROUTINE_DEFINITION {
}

/**
 * External routine meta-type, containing sufficient meta-data to enable a routine in an external library to be called.
 */
export class BMM_EXTERNAL_ROUTINE extends BMM_ROUTINE_DEFINITION {
  /**
   * External call general meta-data, including target routine name, type mapping etc.
   */
  meta_data?: undefined;
  /**
   * Optional argument-mapping meta-data.
   */
  argument_mapping?: undefined;
}

/**
 * A logical group of features, with a name and set of properties that applies to the group.
 */
export class BMM_FEATURE_GROUP {
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Name of this feature group; defaults to 'feature'.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * Set of properties of this group, represented as name/value pairs. These are understood to apply logically to all of the features contained within the group.
   */
  properties?: undefined;
  /**
   * Set of features in this group.
   */
  features?: undefined;
  /**
   * Optional visibility to apply to all features in this group.
   */
  visibility?: BMM_VISIBILITY;
}

/**
 * Abstract parent of visibility representation.
 *
 * TODO: define schemes; probably need to support C++/Java scheme as well as better type-based schemes.
 */
export abstract class BMM_VISIBILITY {
}

/**
 * Meta-type for an automatically created variable referencing the current instance. Typically called 'self' or 'this' in programming languages. Read-only.
 */
export class BMM_SELF extends BMM_READONLY_VARIABLE {
  /**
   * Name of this model element.
   */
  override get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  override get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  override set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }
}

/**
 * Meta-type for static value properties computed once by a function invocation.
 */
export class BMM_SINGLETON extends BMM_STATIC {
  /**
   * Generator of the value of this static property.
   */
  generator?: BMM_ROUTINE_DEFINITION;
}

/**
 * Meta-type for locally declared routine body.
 */
export class BMM_LOCAL_ROUTINE extends BMM_ROUTINE_DEFINITION {
  /**
   * Local variables of the routine, if there is a body defined.
   */
  locals?: undefined;
  /**
   * Body of routine declaration.
   */
  body?: BMM_STATEMENT_BLOCK;
}

/**
 * Enumeration of possible position of operator in a syntactic representation for operators associated with 1- and 2- degree functions.
 */
export class BMM_OPERATOR_POSITION extends openehr_base.String {
}

/**
 * Enumeration of parameter read/write direction values.
 */
export class BMM_PARAMETER_DIRECTION extends openehr_base.String {
}
